/**
 * Returns a new array of studios sorted by name alphabetically (A → Z, case-insensitive).
 *
 * - Safely handles null/undefined inputs by returning an empty array
 * - Creates a new sorted array without modifying the original
 * - Handles missing name properties by treating them as empty strings
 *
 * @param {Array<{name?: string}>} studios - Array of studio objects with optional name property
 * @returns {Array<{name?: string}>} New sorted array of studio objects
 *
 * @example
 * const studios = [
 *   { name: 'Zebra Studio' },
 *   { name: 'Ace Gaming' },
 *   { } // Missing name
 * ];
 *
 * sortStudiosByName(studios);
 * // Returns: [
 * // { }, // Empty name first
 * // { name: 'Ace Gaming' },
 * // { name: 'Zebra Studio' }
 * // ]
 */
export const sortStudiosByName = (studios = []) => {
  if (!Array.isArray(studios)) return []
  // Return a new sorted copy (case-insensitive A -> Z)
  return [...studios].sort((a, b) => {
    const an = (a?.name || '').toString().toLowerCase()
    const bn = (b?.name || '').toString().toLowerCase()
    return an.localeCompare(bn)
  })
}

/**
 * Checks whether a studio matches a search query.
 *
 * - Matches on the tenant display name (`studioDisplayName`) or the original `name`,
 *   so renamed studios are found by both.
 * - Matching is case-insensitive and ignores surrounding whitespace.
 * - An empty query matches every studio.
 *
 * @param {Object} studio
 * @param {string} searchQuery
 * @returns {boolean}
 */
export const matchesStudioSearch = (studio, searchQuery) => {
  const query = (searchQuery || '').trim().toLowerCase()
  if (!query) return true
  return [studio?.studioDisplayName, studio?.name]
    .some(studioName => (studioName || '').toString().toLowerCase().includes(query))
}
